import { parseName } from '@nexelem/utils';
import { memo, PropsWithChildren, useState } from 'react';
import { NodeType, QueueBatchOrTaskNode, QueueMultipleNode } from '../../types';
import { DropdownMenu, Status } from '@nexelem/components';
import { NodeBase } from '../NodeBase';
import clsx from 'clsx';
import { GanttUtils } from '../../../utils';
import { GanttMode, useProductionPlanQueryParams } from '../../../hooks';
import { isSearchEmphasizedSelector, isSearchSelectedSelector, useProductionPlanSearchStore } from '../../../store';
import { MultipleTableAvatar } from '../../../../../molecules/MultipleTableAvatar';
import { useCustomTranslation } from '../../../../../../hooks';
import { TranslationResult } from '../../../../../../utils';
import { ResourceView, TaskStatus } from '@nexelem/graphne-api';
import { GanttConfig } from '../../GanttConfig';
import { TaskMainValueOptions, useProductionPlanSettings } from '../../../components';
import { MultipleNodeDropdownMenuContent } from './MultipleNodeDropdownMenuContent';
import { NodeFlags } from '../NodeFlags';
import { useNodeMultipleFlagsMapper } from '../../mappers';

export type NodeMultipleData = {
  nodeIds?: Array<string | number>;
};

type NodeMultipleProps = {
  node: QueueMultipleNode;
};

export const NodeMultiple = memo(({ node, children }: PropsWithChildren<NodeMultipleProps>) => {
  const [isOpen, setOpen] = useState(false);
  const { queryParams } = useProductionPlanQueryParams();

  const nodeMapper = useNodeMapper();

  const isWorkerMode = queryParams.mode === GanttMode.WORKER;

  const nodeIds = node.overlappingNodes
    .filter((node): node is QueueBatchOrTaskNode => node.type === NodeType.TASK || node.type === NodeType.BATCH)
    .map((node) => ('tasks' in node ? node.tasks?.map((task) => task.id) : node.id))
    .flat()
    .filter((id): id is number => id !== undefined);

  const isSearchEmphasized = useProductionPlanSearchStore(isSearchEmphasizedSelector(nodeIds));
  const isSearchSelected = useProductionPlanSearchStore(isSearchSelectedSelector(nodeIds));

  const {
    firstNodeProductName,
    secondNodeProductName,
    hasOverdue,
    hasCriticalError,
    isUnassigned,
    taskNodes,
    avatars,
    hasAssignedWorkers,
  } = nodeMapper(node);

  const nodeWidth = node.nodeWidth;
  const isSmallNode = nodeWidth <= GanttConfig.SMALL_NODE_WIDTH;

  const isSelected = isOpen || !!(queryParams.taskId && nodeIds.includes(queryParams.taskId));

  const nodeFlags = useNodeMultipleFlagsMapper(node);

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={setOpen}>
      <DropdownMenu.Trigger>
        <NodeBase
          hasError={hasCriticalError}
          isEmphasized={isSearchEmphasized}
          isOverdue={hasOverdue}
          isSearchSelected={isSearchSelected}
          isSelected={isSelected}
          multiple={node.hasBreak ? 'break' : 'default'}
          nodeIds={nodeIds}
          toPublish={nodeFlags.toPublish}
          unassigned={isUnassigned}
          width={nodeWidth}
        >
          <div className={clsx('flex w-full items-center overflow-hidden', isSmallNode && 'justify-center')}>
            {hasCriticalError && <Status className="mr-2 first-of-type:ml-1" color="red" variant="dot" />}
            <div className="ml-1 flex items-center gap-2">
              <span className={clsx('text-s16-sb', !hasCriticalError && !hasOverdue && 'ml-1')}>
                {node.tasksAmount}
              </span>
            </div>
            <NodeFlags className="ml-1" flags={nodeFlags} nodeId={Number(node.id)} />
            {firstNodeProductName && !isSmallNode && (
              <div className="ml-2 flex min-w-0 flex-col">
                {firstNodeProductName && <span className="truncate text-s11-sb">{firstNodeProductName}</span>}
                {secondNodeProductName && <span className="truncate text-s11-sb">{secondNodeProductName}</span>}
              </div>
            )}

            <div className="ml-auto flex items-end">
              {taskNodes.length > 2 && <span className="self-end pl-0.5 text-s11-sb">...</span>}
              {(isWorkerMode || hasAssignedWorkers) && <MultipleTableAvatar avatars={avatars} className="pl-1" />}
            </div>
          </div>
          {children}
        </NodeBase>
      </DropdownMenu.Trigger>
      <MultipleNodeDropdownMenuContent taskNodes={node.overlappingNodes} />
    </DropdownMenu.Root>
  );
});

NodeMultiple.displayName = 'NodeMultiple';

const useNodeMapper = () => {
  const { tWrapper } = useCustomTranslation('products.productionPlan');
  const displayProductName = useProductionPlanSettings((state) => state.taskMainValue === TaskMainValueOptions.PRODUCT);
  const { queryParams } = useProductionPlanQueryParams();
  const showOnTask = useProductionPlanSettings((state) => state.gantt.showOnTask);

  const isWorkerMode = queryParams.mode === GanttMode.WORKER;
  const showAssignedWorkers = isWorkerMode ? true : showOnTask.assignedWorkers;

  return (node: QueueMultipleNode) => {
    const taskNodes = node.overlappingNodes.filter(
      (node): node is QueueBatchOrTaskNode => node.type === NodeType.TASK || node.type === NodeType.BATCH,
    );

    const firstNode = taskNodes.at(0);
    const secondNode = taskNodes.at(1);

    const hasUnassigned = taskNodes.some((node) => node.softResourceAssignments?.length === 0);
    const assignedWorkers = taskNodes.reduce(
      (acc, node) => [...acc, ...(node.softResourceAssignments || [])],
      [] as ResourceView[],
    );
    // Sum raw Double values first, then round up at the end
    const requiredWorkersRaw = taskNodes.reduce((acc, node) => (node.requiredSoftResourcesAmount || 0) + acc, 0);
    const requiredWorkers = requiredWorkersRaw <= 0 ? 0 : requiredWorkersRaw < 1.0 ? 1 : Math.ceil(requiredWorkersRaw);
    const unassignedWorkers =
      requiredWorkers - assignedWorkers.length < 0 ? 0 : requiredWorkers - assignedWorkers.length;

    const avatars = [...assignedWorkers.map(({ name }) => parseName(name)), ...new Array(unassignedWorkers).fill({})];

    const hasAssignedWorkers = !!assignedWorkers.length && showAssignedWorkers;

    return {
      taskNodes,
      firstNodeProductName: getProductName(firstNode, displayProductName, tWrapper),
      secondNodeProductName: getProductName(secondNode, displayProductName, tWrapper),
      hasOverdue: taskNodes.some(hasOverdue),
      hasCriticalError: taskNodes.some(hasCriticalError),
      isUnassigned: isWorkerMode && hasUnassigned,
      avatars,
      hasAssignedWorkers,
      toPublish: hasToPublish(taskNodes),
    };
  };
};

const hasToPublish = (taskNodes: QueueBatchOrTaskNode[]) => {
  const allTasksArePending = taskNodes.every((node) => node.status === TaskStatus.Pending);

  if (!allTasksArePending) {
    return undefined;
  }

  return !taskNodes.every((taskNode) =>
    taskNode.type === NodeType.TASK
      ? taskNode.toPublish === false
      : taskNode.tasks?.every((task) => task.toPublish === false),
  );
};

const getProductName = (
  node: QueueBatchOrTaskNode | undefined,
  displayProductName: boolean,
  tWrapper: TranslationResult<'products.productionPlan'>,
) => {
  if (!node) {
    return undefined;
  }

  if (node.type === NodeType.TASK) {
    return displayProductName ? node.productName : node.name;
  }

  if (node.type === NodeType.BATCH) {
    return `${tWrapper('batch')} ${node.name}`;
  }
};

const hasOverdue = (node: QueueBatchOrTaskNode) => {
  if (node.type === NodeType.TASK) {
    return GanttUtils.hasOverdueError(node.errors);
  }

  if (node.type === NodeType.BATCH) {
    return node.tasks?.some(({ errors }) => GanttUtils.hasOverdueError(errors));
  }

  return false;
};

const hasCriticalError = (node: QueueBatchOrTaskNode) => {
  if (node.type === NodeType.TASK) {
    return GanttUtils.hasCriticalError(node.errors);
  }

  if (node.type === NodeType.BATCH) {
    return node.tasks?.some(({ errors }) => GanttUtils.hasCriticalError(errors));
  }

  return false;
};
